import { inject, Injectable } from '@angular/core';
import { User } from 'app/core/user/user.types';
import { map, Observable, ReplaySubject, tap } from 'rxjs';
import { ApiService } from '../auth/api.service';

@Injectable({providedIn: 'root'})
export class UserService
{
    private _httpClient = inject(ApiService);
    private _user: ReplaySubject<User> = new ReplaySubject<User>(1);
    private _permissions: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for user
     *
     * @param value
     */
    set user(value: User)
    {
        // Store the value
        this._user.next(value);
    }

    get user$(): Observable<User>
    {
        return this._user.asObservable();
    }
    set permissions(value: string[])
    {
        this._permissions.next(value);
    }

    get permissions$(): Observable<string[]>
    {
        return this._permissions.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get the current signed-in user data
     */
    get(): Observable<User>
    {
        return this._httpClient.get('api/auth/me').pipe(
            map((response: { user: User }) => response.user),
            tap((user: User) =>
            {
                this._user.next(user);
            }),
        );
    }

    /**
     * Update the user
     *
     * @param user
     */
    update(user: User): Observable<any>
    {
        return this.updateProfile(user);
    }

    updateProfile(user: Pick<User, 'name' | 'email' | 'phone' | 'avatar' | 'country_id'>): Observable<User>
    {
        return this._httpClient.put('api/auth/profile', user).pipe(
            map((response: { user: User }) => response.user),
            tap((response: User) =>
            {
                this._user.next(response);
            }),
        );
    }

    updatePassword(data: { current_password: string; password: string; password_confirmation: string }): Observable<void>
    {
        return this._httpClient.put('api/auth/password', data);
    }
}
